import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, CheckCircle, XCircle, RefreshCw, Search } from 'lucide-react';
import { deploymentsApi } from '../../api/client';
import { SectionHeader, SignalBadge, EmptyState, Spinner } from '../../components/ui';
import { fmtDateTime, cn } from '../../lib/utils';

// Backend has GET /strategy/deployments/{id}/dispatch-logs (per deployment)
// No global dispatch log endpoint exists — user must enter a deployment ID.

export default function AdminDispatchLogsPage() {
  const [depId, setDepId]     = useState('');
  const [activeId, setActive] = useState('');

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ['dispatchLogs', activeId],
    queryFn:  () => deploymentsApi.getDispatchLogs(activeId),
    enabled:  !!activeId,
    refetchInterval: 10_000,
  });

  const allLogs = logs || [];
  const ok   = allLogs.filter((l) => l.status_code >= 200 && l.status_code < 300).length;
  const fail = allLogs.length - ok;

  return (
    <div className="max-w-7xl space-y-5">
      <SectionHeader
        title="Dispatch Logs"
        subtitle="HTTP alert events — GET /strategy/deployments/{id}/dispatch-logs"
        actions={
          activeId && (
            <button onClick={refetch} className="btn-ghost flex items-center gap-1.5 text-xs">
              <RefreshCw size={12} /> Refresh
            </button>
          )
        }
      />

      {/* Deployment ID input */}
      <div className="panel p-4">
        <div className="text-xs font-mono text-txt-muted uppercase tracking-widest mb-3">
          Select Deployment
        </div>
        <div className="flex items-center gap-3">
          <input
            value={depId}
            onChange={(e) => setDepId(e.target.value)}
            placeholder="Enter deployment ID e.g. 1"
            className="input-base max-w-xs"
            onKeyDown={(e) => e.key === 'Enter' && depId && setActive(depId)}
          />
          <button
            onClick={() => depId && setActive(depId)}
            disabled={!depId}
            className="btn-primary flex items-center gap-1.5"
          >
            <Search size={13} /> Load Logs
          </button>
        </div>
      </div>

      {/* Summary */}
      {activeId && (
        <div className="flex items-center gap-4 text-xs font-mono">
          <span className="text-txt-muted">Deployment #{activeId}</span>
          <span className="flex items-center gap-1.5 text-bull"><CheckCircle size={12} /> {ok} OK</span>
          <span className="flex items-center gap-1.5 text-bear"><XCircle size={12} /> {fail} failed</span>
          <span className="text-txt-muted">{allLogs.length} total</span>
        </div>
      )}

      {/* Table */}
      <div className="panel overflow-hidden">
        {!activeId ? (
          <div className="p-10">
            <EmptyState
              title="Enter a deployment ID above"
              description="Dispatch logs are stored per deployment — enter an ID to view its alert history"
            />
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !allLogs.length ? (
          <div className="p-8">
            <EmptyState title="No dispatch events" description={`No alerts dispatched for deployment #${activeId}`} />
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-bg-border">
                {['Timestamp', 'Direction', 'Symbol', 'Strike', 'HTTP', 'Duration', 'Response'].map((h) => (
                  <th key={h} className="text-left py-3 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allLogs.map((l, i) => {
                const good = l.status_code >= 200 && l.status_code < 300;
                return (
                  <tr key={l.id ?? i} className={cn(
                    'border-b border-bg-border/30 hover:bg-bg-hover',
                    !good && 'bg-bear/5'
                  )}>
                    <td className="py-2.5 px-3 font-mono text-txt-muted whitespace-nowrap">{fmtDateTime(l.dispatched_at)}</td>
                    <td className="py-2.5 px-3"><SignalBadge direction={l.direction} /></td>
                    <td className="py-2.5 px-3 font-mono text-txt-secondary">{l.symbol || '—'}</td>
                    <td className="py-2.5 px-3 font-mono text-txt-secondary">{l.strike || '—'}</td>
                    <td className="py-2.5 px-3">
                      <span className={cn('badge border',
                        good ? 'bg-bull/10 border-bull/30 text-bull' : 'bg-bear/10 border-bear/30 text-bear'
                      )}>
                        {good ? <CheckCircle size={9} /> : <XCircle size={9} />}
                        {l.status_code}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-mono text-txt-muted">{l.duration_ms ? `${l.duration_ms}ms` : '—'}</td>
                    <td className="py-2.5 px-3 font-mono text-txt-muted text-2xs max-w-xs truncate">{l.response || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
